import React from 'react';
import { Controller, FieldErrors, RegisterOptions, useFormContext } from 'react-hook-form';
import { SxProps, TextField, Theme } from '@mui/material';
import InputMask from 'react-input-mask';


interface TextFieldFormControlProps {
    name: string;
    label: string;
    required?: boolean;
    disabled?: boolean;
    style?: React.CSSProperties;
    type?: 'text' | 'number' | 'email' | 'tel';
    maxLength?: number;
    placeholder?: string;
    mask?: string;                           // маска, если нужна
    transform?: (value: string) => string;   // можно править значение перед onChange
    validationRules?: RegisterOptions;
    sx?: SxProps<Theme>;
    step?: string;                           // для number input
}

export const TextFieldFormControl: React.FC<TextFieldFormControlProps> = ({
    name,
    label,
    required = false,
    disabled = false,
    type = 'text',
    style,
    maxLength,
    placeholder,
    mask,
    transform,
    validationRules = {},
    sx,
    step,
    ...otherProps
}) => {
    const { control, formState: { errors } } = useFormContext();

    const getNestedError = (name: string): FieldErrors | undefined => {
        return name.split('.').reduce((acc: any, curr: string) => {
            return acc && acc[curr] ? acc[curr] : undefined;
        }, errors);
    };

    const validateNotOnlySpaces = (value: string) => {
        if (typeof value === 'string') {
            return value.trim().length > 0 || 'Поле не может содержать только пробелы';
        }
        return true;
    };

    const fieldError = getNestedError(name);
    const isError = Boolean(fieldError);

    const rules = {
        required: required ? 'Обязательное поле' : false,
        ...(required && { validate: validateNotOnlySpaces }),
        maxLength: maxLength ? { value: maxLength, message: `Максимальная длина ${maxLength} символов` } : undefined,
        ...validationRules,
    };

    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field }) => {
                // обёртка onChange, чтобы можно было трансформировать value
                const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                    let val = e.target.value;
                    if (transform) {
                        val = transform(val);
                    }
                    field.onChange(val);
                };

                const textField = (
                    <TextField
                        {...otherProps}
                        type={type}
                        label={label}
                        size="small"
                        error={isError}
                        helperText={isError ? (fieldError as any)?.message : ''}
                        required={required}
                        disabled={mask ? undefined : disabled} // Don't pass disabled to TextField when using mask
                        placeholder={placeholder}
                        style={style}
                        fullWidth
                        sx={sx}
                        inputProps={{
                            ...(type === 'number' && step ? { step } : {}),
                            ...(maxLength ? { maxLength } : {})
                        }}
                    />
                );

                if (mask) {
                    return (
                        <InputMask
                            mask={mask}
                            maskChar={null}
                            value={field.value || ''}
                            onChange={handleChange}   // прокидываем сюда
                            onBlur={field.onBlur}
                            disabled={disabled} // Pass disabled directly to InputMask
                        >
                            {(inputMaskProps: any) => (
                                // спредим все нужные inputProps
                                React.cloneElement(textField, {
                                    inputProps: {
                                        ...inputMaskProps,
                                        maxLength, // если нужно
                                    },
                                })
                            )}
                        </InputMask>
                    );
                }

                // без маски — обычный TextField
                return React.cloneElement(textField, {
                    onChange: handleChange,
                    onBlur: field.onBlur,
                    value: field.value ?? '',
                });
            }}
        />
    );
};